'use client';
import cn from 'classnames';
import { CSSProperties, ElementType, MouseEventHandler, ReactNode, useEffect, useState } from 'react';
import { FontIcon, MaskGradient, SiteLogo } from '@intigral/shared';
import styles from './selectprofile.module.sass';

export interface SelectProfileItem {
    id?: string;
    name: string;
    rating?: string;
    avatar?: string;
    color?: string;
}

export interface SelectProfileHero {
    heroSrc?: string;
    trailer?: ReactNode;
    providerLogoSrc?: string;
    titleLogoSrc?: string;
    title?: string;
}

export interface SelectProfileLanguage {
    label: string;
    href: string;
    onClick?: MouseEventHandler<HTMLAnchorElement>;
}

interface Slide {
    key: string;
    promo: SelectProfileHero;
    ready: boolean;
    first?: boolean;
    hold: number;
    fresh?: { provider: boolean; title: boolean };
}

// Figma BBT-142 prototype timing
const BACKDROP_FADE_MS = 200;
const LOGO_DELAY_MS = 240;
const LOGO_FADE_MS = 300;
const ZOOM_DELAY_MS = 60;
const ZOOM_MS = 4000;
const FIRST_ZOOM_MS = 6000;
const ZOOM_START_MS = BACKDROP_FADE_MS + LOGO_DELAY_MS + LOGO_FADE_MS + ZOOM_DELAY_MS;
const SLIDE_MS = ZOOM_START_MS + ZOOM_MS + ZOOM_DELAY_MS;
const FIRST_SLIDE_MS = ZOOM_DELAY_MS + FIRST_ZOOM_MS + ZOOM_DELAY_MS;
const zoomStart = (first?: boolean) => (first ? ZOOM_DELAY_MS : ZOOM_START_MS);
const zoomLength = ({ first, hold }: Slide) => Math.max(0, hold - zoomStart(first) - ZOOM_DELAY_MS);

const provider = (promo: SelectProfileHero) => promo.providerLogoSrc;
const titleArt = (promo: SelectProfileHero) => promo.titleLogoSrc ?? promo.title;
const freshAgainst = (promo: SelectProfileHero, below: SelectProfileHero) => ({
    provider: provider(promo) !== provider(below),
    title: titleArt(promo) !== titleArt(below),
});

export interface SelectProfileProps {
    profiles: SelectProfileItem[];
    promos?: SelectProfileHero[];
    /** ms per promo, e.g. CMS promoSliderSPL.nextSlideDelay */
    slideDelay?: number;
    prompt: string;
    addLabel: string;
    manageLabel?: string;
    editing?: boolean;
    language?: SelectProfileLanguage;
    logoSrc?: string;
    logoHref?: string;
    LinkComponent?: ElementType;
    onSelect?: (profile: SelectProfileItem) => void;
    /** without it the add tile is hidden, e.g. at the profile limit */
    onAdd?: () => void;
    onManage?: () => void;
}

export const SelectProfile = ({
    profiles,
    prompt,
    addLabel,
    manageLabel,
    editing = false,
    language,
    promos = [],
    slideDelay,
    logoSrc,
    logoHref = '/',
    LinkComponent = 'a',
    onSelect,
    onAdd,
    onManage,
}: SelectProfileProps) => {
    const [promoIndex, setPromoIndex] = useState(0);
    const promo: SelectProfileHero = promos[promoIndex % promos.length] ?? {};
    const key = [promo.heroSrc, promo.providerLogoSrc, promo.titleLogoSrc, promo.title].join('|');
    const current = key === '|||' ? undefined : key;

    const holdFor = (first: boolean) => slideDelay ?? (first ? FIRST_SLIDE_MS : SLIDE_MS);
    const [slides, setSlides] = useState<Slide[]>(
        current ? [{ key: current, promo, ready: true, first: true, hold: holdFor(true) }] : []
    );
    if (current !== slides[slides.length - 1]?.key) {
        const at = current ? slides.findIndex((slide) => slide.key === current) : -1;
        const below = [...slides].reverse().find((slide) => slide.ready)?.promo ?? promo;
        setSlides(
            !current
                ? []
                : at >= 0
                ? slides.slice(0, at + 1)
                : [
                      ...slides,
                      {
                          key: current,
                          promo,
                          ready: !promo.heroSrc,
                          first: !slides.length,
                          hold: holdFor(!slides.length),
                          fresh: promo.heroSrc ? undefined : freshAgainst(promo, below),
                      },
                  ]
        );
    }
    const markReady = (slideKey: string) =>
        setSlides((stack) => {
            const at = stack.findIndex((slide) => slide.key === slideKey);
            const below =
                stack
                    .slice(0, at)
                    .reverse()
                    .find((slide) => slide.ready)?.promo ?? stack[at]?.promo;
            return stack.map((slide, index) =>
                index === at ? { ...slide, ready: true, fresh: freshAgainst(slide.promo, below ?? slide.promo) } : slide
            );
        });
    const settle = (slideKey: string) =>
        setSlides((stack) => {
            const at = stack.findIndex((slide) => slide.key === slideKey);
            return at > 0 ? stack.slice(at) : stack;
        });
    const fadeIn = (index: number) => index > 0 && cn(styles.incoming, slides[index].ready && styles.fading);
    const shown = slides.reduce((last, slide, index) => (slide.ready ? index : last), 0);
    const fadeLogo = (index: number, which: 'provider' | 'title') => {
        const slide = slides[index];
        if (!slide.ready || index > shown) return styles.waiting;
        if (index < shown) return slides[shown].fresh?.[which] ? styles.outgoing : undefined;
        return slide.fresh?.[which] ? styles['logo-in'] : undefined;
    };

    const shownKey = slides[shown]?.key;
    const shownHold = slides[shown]?.hold;
    useEffect(() => {
        if (promos.length < 2 || !shownKey || !shownHold) return;
        const hold = shownHold;
        let timer = 0;
        const next = () => {
            if (document.hidden) timer = window.setTimeout(next, hold);
            else setPromoIndex((index) => (index + 1) % promos.length);
        };
        timer = window.setTimeout(next, hold);
        return () => window.clearTimeout(timer);
    }, [shownKey, shownHold, promos.length]);

    return (
        <section
            className={styles.screen}
            style={
                {
                    '--backdrop-fade': `${BACKDROP_FADE_MS}ms`,
                    '--logo-delay': `${BACKDROP_FADE_MS + LOGO_DELAY_MS}ms`,
                    '--logo-fade': `${LOGO_FADE_MS}ms`,
                } as CSSProperties
            }
        >
            <div className={styles.hero}>
                {slides.map(
                    (slide, index) =>
                        slide.promo.heroSrc && (
                            <div
                                key={slide.key}
                                className={cn(styles.backdrop, fadeIn(index))}
                                onAnimationEnd={() => settle(slide.key)}
                            >
                                <img
                                    src={slide.promo.heroSrc}
                                    alt=""
                                    className={cn(slide.ready && styles.zoom)}
                                    style={
                                        {
                                            '--zoom': `${zoomLength(slide)}ms`,
                                            '--zoom-delay': `${zoomStart(slide.first)}ms`,
                                        } as CSSProperties
                                    }
                                    onLoad={() => markReady(slide.key)}
                                />
                            </div>
                        )
                )}
                {promo.trailer && <div className={styles.trailer}>{promo.trailer}</div>}
            </div>

            <div className={styles.stage}>
                <div className={styles.mask} aria-hidden>
                    <MaskGradient />
                </div>

                <header className={styles.chrome}>
                    {logoSrc && (
                        <span className={styles.logo}>
                            <SiteLogo
                                homeUrl={logoHref}
                                LinkComponent={LinkComponent}
                                title="stc tv"
                                logoSrc={logoSrc}
                            />
                        </span>
                    )}
                    {manageLabel && (
                        <button type="button" className={styles.manage} onClick={onManage}>
                            {manageLabel}
                            <FontIcon name={editing ? 'check-lg' : 'pencil'} size="x-small" md="x-small" />
                        </button>
                    )}
                    {language && (
                        <LinkComponent href={language.href} className={styles.manage} onClick={language.onClick}>
                            {language.label}
                            <FontIcon name="globe" size="x-small" md="x-small" />
                        </LinkComponent>
                    )}
                </header>

                <div className={styles.deck}>
                    <div className={styles.marquees}>
                        {slides.map(({ key: slideKey, promo: { providerLogoSrc, titleLogoSrc, title } }, index) => (
                            <div
                                key={slideKey}
                                className={styles.marquee}
                                aria-hidden={index !== shown || undefined}
                                onAnimationEnd={() => settle(slideKey)}
                            >
                                {providerLogoSrc && (
                                    <img
                                        className={cn(styles.brand, fadeLogo(index, 'provider'))}
                                        src={providerLogoSrc}
                                        alt=""
                                    />
                                )}
                                {titleLogoSrc ? (
                                    <img
                                        className={cn(styles.art, fadeLogo(index, 'title'))}
                                        src={titleLogoSrc}
                                        alt={title}
                                    />
                                ) : (
                                    title && <h1 className={cn(styles.headline, fadeLogo(index, 'title'))}>{title}</h1>
                                )}
                            </div>
                        ))}
                    </div>

                    <p className={styles.prompt}>{prompt}</p>
                </div>
            </div>

            <div className={styles.dock}>
                <div className={styles.tray}>
                    <div className={styles.row}>
                        {profiles.map((profile) => (
                            <button
                                key={profile.id ?? profile.name}
                                type="button"
                                className={cn(styles.profile, editing && styles.editing)}
                                onClick={() => onSelect?.(profile)}
                            >
                                <img
                                    className={styles.face}
                                    src={profile.avatar}
                                    alt=""
                                    style={{ backgroundColor: profile.color }}
                                />
                                <span className={styles.person}>{profile.name}</span>
                                {profile.rating && <span className={styles.rating}>{profile.rating}</span>}
                                {editing && (
                                    <span className={styles.pencil} aria-hidden>
                                        <FontIcon name="pencil" size="medium" md="medium" />
                                    </span>
                                )}
                            </button>
                        ))}
                        {onAdd && (
                            <button type="button" className={styles.add} onClick={onAdd}>
                                <span className={styles.plus}>
                                    <FontIcon name="plus" size="smaller" md="smaller" />
                                </span>
                                <span className={styles.hint}>{addLabel}</span>
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </section>
    );
};
